<template>
  <div
    class="plain-text"
    :style="computedStyle"
    :class="{ 'is-constrained': needsOverflowCheck, 'has-scroll': hasScroll }"
  >
    <p
      ref="content"
      v-html="fieldsData.label"
    ></p>
  </div>
</template>

<script>
/**
 * Renders a `plain_text` form field (used e.g. as a form step header or
 * free-form instructional text).
 *
 * When the field has a height constraint configured (`fitToScreen`,
 * `fixedHeight` or `maxHeight`), the block can get its own internal
 * scrollbar. In that case we show a subtle box-shadow "border" to make it
 * visually clear that the box scrolls internally.
 *
 * IMPORTANT: the overflow check below is intentionally designed so that
 * toggling `has-scroll` can NEVER change this element's own box size
 * (scrollHeight/clientHeight/offsetHeight):
 *  - `is-constrained` (which reserves the padding/border-radius space) is
 *    derived purely from static field settings, not from the measurement,
 *    so it never flips as a side effect of measuring.
 *  - `has-scroll` only toggles a `box-shadow`, which is a paint effect and
 *    does not participate in layout at all.
 *  - The ResizeObserver measures the inner, unstyled content node
 *    (`scrollHeight`) against the outer container's `clientHeight`,
 *    decoupling "what we observe" from "what we restyle".
 * Without these safeguards, a border/padding toggle driven by a
 * ResizeObserver watching its own styled element creates a self-triggering
 * resize feedback loop (the element's size change re-fires the observer),
 * which is what previously caused the box to flicker a border and the
 * page's scrollbar to jump.
 */
export default {
  name: 'PlainTextField',
  props: {
    fieldsData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      hasScroll: false,
      resizeObserver: null,
    }
  },
  computed: {
    computedStyle() {
      return this.getPlainTextStyle(this.fieldsData)
    },
    needsOverflowCheck() {
      const settings = this.fieldsData?.settings
      return !!(settings?.fitToScreen || settings?.fixedHeight || settings?.maxHeight)
    },
  },
  watch: {
    fieldsData: {
      deep: true,
      handler() {
        this.$nextTick(() => this.updateScrollState())
      },
    },
    needsOverflowCheck(val) {
      if (val) {
        this.$nextTick(() => this.updateScrollState())
      } else {
        this.hasScroll = false
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (typeof ResizeObserver === 'undefined') return

      const container = this.$el
      const content = this.$refs.content
      if (!container || !content) return

      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollState()
      })

      this.resizeObserver.observe(container)
      this.resizeObserver.observe(content)

      this.updateScrollState()
    })
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    updateScrollState() {
      if (!this.needsOverflowCheck) {
        this.hasScroll = false
        return
      }

      const container = this.$el
      const content = this.$refs.content
      if (!container || !content) return

      if (content.scrollHeight === 0 && container.clientHeight === 0) return

      const hasOverflow = content.scrollHeight > container.clientHeight

      if (hasOverflow !== this.hasScroll) {
        this.hasScroll = hasOverflow
      }
    },
    getPlainTextStyle(field) {
      const style = {}

      if (field?.settings?.fitToScreen) {
        style['max-height'] = '60vh'
        style['overflow-y'] = 'auto'

        return style
      }

      if (field?.settings?.fixedHeight) {
        style.height = `${field.settings.fixedHeight}px`
        style['overflow-y'] = 'auto'
      }

      if (field?.settings?.maxHeight) {
        style['max-height'] = typeof field.settings.maxHeight === 'number'
          ? `${field.settings.maxHeight}px`
          : field.settings.maxHeight
        style['overflow-y'] = 'auto'
      }

      return style
    },
  },
}
</script>

<style lang="scss">
@import 'src/assets/scss/colors/colors';

.plain-text {
  margin: 30px 0;
  word-break: break-word;

  h1, h2, h3, h4, h5, h6, p, em, img, strong, sub, sup, b, u, i, dl, dt, dd, ol, ul, li, fieldset, form, label, table, tbody, tfoot, thead, tr, th, td, article, aside, canvas, details, figcaption, figure, a, footer, header, hgroup, menu, nav, section, summary, time, mark, audio, video {
    margin: revert;
    padding: revert;
    font-size: revert;
    list-style: revert;
  }

  img, video {
    max-width: 100%;
  }

  // Reserved permanently whenever the field is *configured* to be height
  // constrained (static, derived from settings) - never toggles based on
  // the overflow measurement, so it can't change the element's own box
  // size as a side effect of that measurement.
  &.is-constrained {
    padding: 10px;
    border-radius: 4px;
  }

  // Purely a paint effect (no layout box impact) - safe to toggle based on
  // the overflow measurement without ever affecting scrollHeight/clientHeight.
  &.has-scroll {
    box-shadow: 0 0 0 1px rgba($secondary, 0.3);
  }

  &::-webkit-scrollbar {
    width: 6px;
    height: 90%;
  }

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px #fff;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: $primary;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #69182d;
  }
}
</style>

